import { Tooltip } from '@tableau/widgets';
import React from 'react';

import { elideTextStyle } from '../../style/LayoutStyles';
import { cssClass } from '../../style/StyleFunctions';
import { TypedBodyCellProps } from '../TypedDataGridInterfaces';
import { cellDisabledStyle, cellPaddingStyle } from './CellStyles';
import { PlaceholderTextCell } from './PlaceholderTextCell';

const textCellClass = cssClass('textCell', elideTextStyle, cellPaddingStyle, { display: 'flex' });
const disabledTextCellClass = cssClass(
  'disabledTextCell',
  elideTextStyle,
  cellPaddingStyle,
  cellDisabledStyle,
  { display: 'flex' });
const noPaddingTextCellClass = cssClass('noPaddingTextCell', elideTextStyle, { display: 'flex' });
const textContainerClass = cssClass('textContainerClass', {
  textOverflow: 'ellipsis',
  overflow: 'hidden'
});
const postIconClass = cssClass('postIconClass', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingLeft: '8px',
});

interface TextCellFormatterProps {
  readonly text: string | null;
  readonly cellTitle?: string | null;
  readonly postIcon?: JSX.Element | null;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly noPadding?: boolean;
  readonly useWidgetsTooltip?: boolean;
}

/**
 * Formatter for a text cell, with optional "icon" content aligned to the right.
 * For a cell with a hyperlink and optional icon, use LinkCellFormatter.
 */
export function TextCellFormatter(props: TextCellFormatterProps) {
  let className = textCellClass;
  if (props.disabled) {
    className = disabledTextCellClass;
  } else if (props.noPadding) {
    className = noPaddingTextCellClass;
  }

  if (props.text) {
    const title = props.cellTitle ?? props.text;
    const textWithTooltip = props.useWidgetsTooltip
      ? <Tooltip getContent={() => <span>{title}</span>}><span>{props.text}</span></Tooltip>
      : props.text;

    return (
      <div className={className} title={props.useWidgetsTooltip ? undefined : title}>
        <div className={textContainerClass}>
          {textWithTooltip}
        </div>
        {props.postIcon
          ? <div className={postIconClass}>{props.postIcon}</div>
          : null
        }
      </div>
    );
  } else if (props.placeholder) {
    return <PlaceholderTextCell placeholderText={props.placeholder} />;
  } else {
    return <div className={props.disabled ? disabledTextCellClass : textCellClass} />; // so empty columns select row in connect-to grids
  }
}

export function createTextCellFormatter<T>(
  getTextFromRowData: (rowData: T) => string | null,
  placeholder?: string,
  getDisabledFromRowData?: (rowData: T) => boolean,
  noPadding = false,
  getPostIconFromRowData?: (rowData: T) => JSX.Element | null,
  getCellTitleFromRowData?: (rowData: T) => string | null
): React.Factory<TypedBodyCellProps<T>> {
  // eslint-disable-next-line react/display-name
  return (props: TypedBodyCellProps<T>) => (
    <TextCellFormatter
      text={getTextFromRowData(props.row.data)}
      cellTitle={getCellTitleFromRowData
        ? getCellTitleFromRowData(props.row.data)
        : getTextFromRowData(props.row.data)}
      postIcon={getPostIconFromRowData ? getPostIconFromRowData(props.row.data) : null}
      placeholder={placeholder}
      disabled={getDisabledFromRowData ? getDisabledFromRowData(props.row.data) : false}
      noPadding={noPadding}
    />
  );
}
